import { AiPanelReference, DualShape } from '../../../../../types/geneCodes/AiPanelReportTypes';
import { LiteratureResult } from '../../../../../types/research/ResearchTypes';

export interface PanelCitation {
	index: number;
	citation: string;
	relevance: string | null;
	title: string | null;
	author: string | null;
	year: string | null;
}

export interface VerifiedCitation extends PanelCitation {
	pmid: string | null;
	url: string | null;
}

// The literature endpoint caps `q` at 2048 chars and `size` at 100.
export const CITATION_QUERY_MAX_CHARS = 2048;
export const CITATION_QUERY_SIZE = 50;

// Below this a "one title contains the other" test stops meaning anything.
const MIN_CONTAINS_CHARS = 20;

const YEAR_IN_PARENS = /\((\d{4})[a-z]?\)/;
const BARE_YEAR = /\b(1[89]\d{2}|20\d{2})\b/;

const normalise = (value: string): string =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const firstSegment = (value: string): string | null => {
	const segment = value
		.replace(/^[\s.,:;]+/, '')
		.split(/\.\s+/)[0]
		.trim()
		.replace(/[.,;:]+$/, '');
	return segment.length > 0 ? segment : null;
};

const firstAuthorSurname = (head: string): string | null => {
	const cleaned = head.replace(/\bet\s+al\.?/gi, ' ').trim();
	const match = cleaned.match(/[A-Za-z][A-Za-z'-]{1,}/);
	return match ? match[0] : null;
};

// Parses the "Author et al. (2023). Title. Journal. DOI" shape the schema asks for, falling back
// to positional segments when the model answers in some other order.
export const parseCitation = (raw: string): Pick<PanelCitation, 'title' | 'author' | 'year'> => {
	const parenMatch = raw.match(YEAR_IN_PARENS);
	if (parenMatch && parenMatch.index !== undefined) {
		return {
			year: parenMatch[1],
			author: firstAuthorSurname(raw.slice(0, parenMatch.index)),
			title: firstSegment(raw.slice(parenMatch.index + parenMatch[0].length)),
		};
	}
	const segments = raw.split(/\.\s+/).map((segment) => segment.trim()).filter(Boolean);
	const bareYear = raw.match(BARE_YEAR);
	return {
		year: bareYear ? bareYear[1] : null,
		author: segments.length > 0 ? firstAuthorSurname(segments[0]) : null,
		title: segments.length > 1 ? segments[1].replace(/[.,;:]+$/, '') : null,
	};
};

export const collectCitations = (references?: Array<DualShape<AiPanelReference>>): PanelCitation[] => {
	const entries = Array.isArray(references) ? references : [];
	const collected: PanelCitation[] = [];
	entries.forEach((entry, index) => {
		let citation: string | null = null;
		let relevance: string | null = null;
		if (typeof entry === 'string') {
			citation = entry.trim() || null;
		} else if (entry && typeof entry === 'object') {
			citation = typeof entry.citation === 'string' ? entry.citation.trim() || null : null;
			relevance = typeof entry.relevance === 'string' ? entry.relevance.trim() || null : null;
		}
		if (!citation) return;
		collected.push({ index, citation, relevance, ...parseCitation(citation) });
	});
	return collected;
};

// ONE request per report: every citation's title goes into a single query.
export const buildCitationQuery = (citations: PanelCitation[]): string => {
	const terms: string[] = [];
	let length = 0;
	citations.forEach((entry) => {
		const term = entry.title ?? entry.citation;
		if (!term) return;
		const addition = terms.length === 0 ? term.length : term.length + 1;
		if (length + addition > CITATION_QUERY_MAX_CHARS) return;
		terms.push(term);
		length += addition;
	});
	return terms.join(' ');
};

const titleMatches = (citationTitle: string, resultTitle: string): boolean => {
	if (citationTitle === resultTitle) return true;
	if (citationTitle.length >= MIN_CONTAINS_CHARS && resultTitle.includes(citationTitle)) return true;
	if (resultTitle.length >= MIN_CONTAINS_CHARS && citationTitle.includes(resultTitle)) return true;
	return false;
};

const resultMatches = (citation: PanelCitation, result: LiteratureResult): boolean => {
	if (!citation.title || !citation.author || !citation.year) return false;
	const resultTitle = normalise(typeof result.title === 'string' ? result.title : '');
	if (!resultTitle || !titleMatches(normalise(citation.title), resultTitle)) return false;

	const meta = result.meta ?? {};
	const resultYear = String(meta.pub_year ?? '');
	if (!resultYear.includes(citation.year)) return false;

	const authors = Array.isArray(meta.authors) ? meta.authors.join(' ') : '';
	const haystack = normalise(`${authors} ${typeof result.subtitle === 'string' ? result.subtitle : ''}`);
	return haystack.includes(normalise(citation.author));
};

// A citation that does not resolve is NOT rendered — a fabricated paper with a plausible title is
// indistinguishable from a real one once it is on a practitioner's screen.
export const verifyCitations = (
	citations: PanelCitation[],
	results: LiteratureResult[],
): VerifiedCitation[] => {
	const candidates = Array.isArray(results) ? results : [];
	const verified: VerifiedCitation[] = [];
	citations.forEach((citation) => {
		const hit = candidates.find((result) => resultMatches(citation, result));
		if (!hit) return;
		const pmid = typeof hit.meta?.pmid === 'string' ? hit.meta.pmid : null;
		verified.push({
			...citation,
			pmid,
			url: hit.url ?? (pmid ? `https://pubmed.ncbi.nlm.nih.gov/${pmid}/` : null),
		});
	});
	return verified;
};
